import { US_REGION_IDS, CAN_REGION_IDS, MX_REGION_IDS } from '../constants/states.js';

export const STAT_SCOPES = ['US', 'CAN', 'MX', 'COMBINED'];

export const SCOPE_DENOMINATORS = {
	US: 51,
	CAN: 13,
	MX: 32,
	COMBINED: 96,
};

/**
 * @param {string} id
 * @returns {'US' | 'CAN' | 'MX' | null}
 */
export function regionScopeForId(id) {
	if (!id || typeof id !== 'string') return null;
	if (US_REGION_IDS.has(id)) return 'US';
	if (CAN_REGION_IDS.has(id)) return 'CAN';
	if (MX_REGION_IDS.has(id)) return 'MX';
	return null;
}

/**
 * @param {string} scope
 * @param {string} id
 * @returns {boolean}
 */
export function isIdInScope(id, scope) {
	const regionScope = regionScopeForId(id);
	if (!regionScope) return false;
	if (scope === 'COMBINED') return true;
	return regionScope === scope;
}

/**
 * @param {string[]} visitedStates
 * @param {'US' | 'CAN' | 'MX' | 'COMBINED'} scope
 * @returns {string[]}
 */
export function filterVisitedByScope(visitedStates, scope) {
	if (!Array.isArray(visitedStates)) return [];
	const seen = new Set();
	const result = [];
	for (const id of visitedStates) {
		if (!id || seen.has(id)) continue;
		if (!isIdInScope(id, scope)) continue;
		seen.add(id);
		result.push(id);
	}
	return result;
}

/**
 * @param {number} count
 * @param {number} denominator
 * @returns {{ percent: string, percentNumber: number }}
 */
export function formatScopePercent(count, denominator) {
	if (!denominator || denominator <= 0) {
		return { percent: '0.00', percentNumber: 0 };
	}
	const percentNumber = Math.min(100, (count / denominator) * 100);
	return { percent: percentNumber.toFixed(2), percentNumber };
}

/**
 * @param {string[]} visitedStates
 * @param {'US' | 'CAN' | 'MX' | 'COMBINED'} scope
 * @param {Partial<Record<'US' | 'CAN' | 'MX' | 'COMBINED', number>> | null} [denominators]
 * @returns {{ scope: string, count: number, denominator: number, percent: string, percentNumber: number }}
 */
export function computeScopeProgress(visitedStates, scope, denominators) {
	const filtered = filterVisitedByScope(visitedStates, scope);
	const count = filtered.length;
	const denominator = denominators?.[scope] ?? SCOPE_DENOMINATORS[scope] ?? 1;
	const { percent, percentNumber } = formatScopePercent(count, denominator);
	return { scope, count, denominator, percent, percentNumber };
}

/**
 * @param {string[]} visitedStates
 * @param {Partial<Record<'US' | 'CAN' | 'MX' | 'COMBINED', number>> | null} [denominators]
 * @returns {Record<'US' | 'CAN' | 'MX' | 'COMBINED', { scope: string, count: number, denominator: number, percent: string, percentNumber: number }>}
 */
export function computeAllScopeProgress(visitedStates, denominators) {
	/** @type {Record<string, ReturnType<typeof computeScopeProgress>>} */
	const stats = {};
	for (const scope of STAT_SCOPES) {
		stats[scope] = computeScopeProgress(visitedStates, scope, denominators);
	}
	return stats;
}

/**
 * US-scope progress string for persisted game state.
 * @param {string[]} visitedStates
 * @param {Partial<Record<'US' | 'CAN' | 'MX' | 'COMBINED', number>> | null} [denominators]
 * @returns {string}
 */
export function usProgressPercent(visitedStates, denominators) {
	return computeScopeProgress(visitedStates, 'US', denominators).percent;
}
